'use client';

import { useState } from 'react';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useTrackFirstView } from '@ifixit/analytics/hooks/use-track-first-view';
import { useTranslations } from '@ifixit/i18n/hooks';
import { Input } from '@ifixit/ui/components/input';
import { cn } from '@ifixit/ui/lib/utils';
import { FixbotIcon } from './fixbot-icon';

const FIXBOT_BASE_URL = 'https://fixbot.ifixit.com/';

function buildFixbotUrl(contentType: string, contentId: number, message?: string) {
   const url = new URL(FIXBOT_BASE_URL);
   if (contentType === 'question') {
      url.searchParams.set('questionid', String(contentId));
   } else {
      url.searchParams.set('type', contentType);
      url.searchParams.set('id', String(contentId));
   }
   if (message) {
      url.searchParams.set('message', message);
   }
   return url.toString();
}

export function ButtonVariant({
   contentType,
   contentId,
   analyticsCategory,
   translationNamespace,
}: FixbotCTAProps) {
   const t = useTranslations(translationNamespace as 'FixbotCTA');
   const href = buildFixbotUrl(contentType, contentId);

   return (
      <a
         href={href}
         onClick={() =>
            trackAnalyticsCustomEvent({
               eventCategory: analyticsCategory,
               eventAction: 'Fixbot CTA - Button Click',
               eventName: 'Fixbot CTA',
            })
         }
         className={cn(
            'flex flex-col items-center gap-4 rounded-lg border border-gray-300 bg-white p-4 no-underline',
            'md:flex-row'
         )}
      >
         <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-brand-100">
               <FixbotIcon className="text-brand-300" style={{ width: 22, height: 22 }} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
               <p className="text-sm font-semibold text-gray-900">{t('didntFindProblem')}</p>
               <p className="text-sm text-gray-900">{t('describeForFix')}</p>
            </div>
         </div>
         <span
            className={cn(
               'inline-flex h-8 shrink-0 items-center justify-center rounded-lg bg-brand-500 px-6 py-[9px]',
               'text-sm font-medium text-white',
               'hover:bg-brand-500/90 transition-colors',
               'w-full sm:w-60 md:h-9 md:w-auto'
            )}
         >
            {t('describeIt')}
         </span>
      </a>
   );
}

function InputVariant({
   contentType,
   contentId,
   analyticsCategory,
   translationNamespace,
}: FixbotCTAProps) {
   const t = useTranslations(translationNamespace as 'FixbotCTA');
   const [message, setMessage] = useState('');
   const href = buildFixbotUrl(contentType, contentId, message || undefined);

   return (
      <div className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-4">
         <p className="text-sm font-semibold text-gray-900">{t('describeProblem')}</p>
         <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-3">
            <Input
               type="text"
               value={message}
               onChange={e => setMessage(e.target.value)}
               placeholder={t('placeholder')}
               className="sm:flex-1"
            />
            <a
               href={href}
               onClick={() =>
                  trackAnalyticsCustomEvent({
                     eventCategory: analyticsCategory,
                     eventAction: 'Fixbot CTA - Input Submit',
                     eventName: 'Fixbot CTA',
                  })
               }
               className={cn(
                  'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg bg-brand-500 px-6 py-[9px]',
                  'text-sm font-medium text-white no-underline',
                  'hover:bg-brand-500/90 transition-colors',
                  'w-full sm:w-auto'
               )}
            >
               {t('askFixbot')}
               <FixbotIcon style={{ width: 22, height: 22 }} />
            </a>
         </div>
      </div>
   );
}

export interface FixbotCTAProps {
   contentType: string;
   contentId: number;
   analyticsCategory: string;
   translationNamespace: string;
   variant?: 'input' | 'button';
}

export function FixbotCTA({ variant = 'input', ...props }: FixbotCTAProps) {
   const ctaRef = useTrackFirstView<HTMLDivElement>(
      () => ({
         eventCategory: props.analyticsCategory,
         eventAction: 'Fixbot CTA - Viewport Visible',
         eventName: variant,
      }),
      { dwellMs: 200 }
   );

   const Variant = variant === 'button' ? ButtonVariant : InputVariant;

   return (
      <div ref={ctaRef}>
         <Variant {...props} />
      </div>
   );
}
